// Temporary mock of QuestionRenderer component
// This should be replaced with the actual @tibbbi-health/questionnaire-components package

import React from 'react';
import {
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Button,
  Box,
  Typography
} from '@mui/material';

interface Question {
  id: string;
  type: string;
  question: string;
  required?: boolean;
  options?: string[];
}

interface QuestionRendererProps {
  question: Question;
  value: any;
  onChange: (value: any) => void;
  onNext?: () => void;
  hasNextButton?: boolean;
  readonly?: boolean; // Add readonly prop for compatibility
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  value,
  onChange,
  onNext,
  hasNextButton = false,
  readonly = false,
}) => {
  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <TextField
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your answer..."
            disabled={readonly}
            sx={{ mt: 1 }}
          />
        );
      
      case 'single-choice':
        return (
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <RadioGroup
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            >
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio disabled={readonly} />}
                  label={option}
                  sx={{ 
                    mb: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    margin: '4px 0',
                    padding: '8px 12px',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      
      case 'multiple-choice':
        return (
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormGroup>
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={Array.isArray(value) && value.includes(option)}
                      onChange={(e) => {
                        const currentValues = Array.isArray(value) ? value : [];
                        if (e.target.checked) {
                          onChange([...currentValues, option]);
                        } else {
                          onChange(currentValues.filter((v) => v !== option));
                        }
                      }}
                      disabled={readonly}
                    />
                  }
                  label={option}
                  sx={{ 
                    mb: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    margin: '4px 0',
                    padding: '8px 12px',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>
        );
      
      case 'numeric':
      case 'pain-scale':
        return (
          <TextField
            type="number"
            fullWidth
            variant="outlined"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{
              min: question.type === 'pain-scale' ? 0 : undefined,
              max: question.type === 'pain-scale' ? 10 : undefined,
            }}
            placeholder={question.type === 'pain-scale' ? "0-10" : "Enter a number..."}
            disabled={readonly}
            sx={{ mt: 1 }}
          />
        );
      
      case 'date':
        return (
          <TextField
            type="date"
            fullWidth
            variant="outlined"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readonly}
            sx={{ mt: 1 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );
      
      case 'body-map':
        return (
          <Box sx={{ mt: 1, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please select the areas on your body where you are experiencing symptoms:
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={Array.isArray(value) ? value.join(', ') : (value || '')}
              onChange={(e) => {
                // For now, convert text input to array format expected by backend
                const areas = e.target.value.split(',').map(area => area.trim()).filter(area => area);
                onChange(areas);
              }}
              placeholder="Describe the location of your symptoms (e.g., lower back, right knee, chest)..."
              disabled={readonly}
              helperText="Separate multiple areas with commas"
            />
          </Box>
        );
      
      default:
        return (
          <TextField
            fullWidth
            variant="outlined"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter your answer..."
            disabled={readonly}
            sx={{ mt: 1 }}
          />
        );
    }
  };

  return (
    <Box>
      {renderInput()}
      {hasNextButton && (
        <Button
          variant="contained"
          color="primary"
          onClick={onNext}
          disabled={readonly}
          sx={{ mt: 2 }}
        >
          Next
        </Button>
      )}
    </Box>
  );
};

export default QuestionRenderer;